/** Popovers, dialogs (modal on desktop, bottom sheet on phones), tooltips and switches. */
import { Dialog as D, Popover as P, Switch as S, Tooltip as T } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { IconButton } from './button';

export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;
export const PopoverClose = P.Close;

export function PopoverContent({
  className,
  align = 'start',
  sideOffset = 4,
  ...props
}: ComponentProps<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(
          // Never taller than the room Radix measured on screen; long content scrolls instead.
          'z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto scroll-thin rounded-md bg-raised p-2 text-ink shadow-menu outline-none',
          className,
        )}
        {...props}
      />
    </P.Portal>
  );
}

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export interface DialogContentProps extends ComponentProps<typeof D.Content> {
  title: string;
  /** Visually hide the title (it stays available to screen readers). */
  hideTitle?: boolean;
  /** "modal" centres a card; "sheet" slides up from the bottom (phones). "auto" picks by width. */
  variant?: 'modal' | 'sheet' | 'auto';
  width?: number;
  showClose?: boolean;
  children: ReactNode;
}

export function DialogContent({
  title,
  hideTitle,
  variant = 'auto',
  width = 560,
  showClose = true,
  className,
  children,
  ...props
}: DialogContentProps) {
  const sheet =
    variant === 'sheet'
      ? 'inset-x-0 bottom-0 top-auto max-h-[92dvh] rounded-t-lg rounded-b-none'
      : variant === 'auto'
        ? 'max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:max-h-[92dvh] max-md:w-full! max-md:max-w-none max-md:translate-x-0 max-md:rounded-t-lg max-md:rounded-b-none md:top-[8vh] md:left-1/2 md:-translate-x-1/2'
        : 'top-[8vh] left-1/2 -translate-x-1/2';
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-40 bg-overlay" />
      <D.Content
        aria-describedby={undefined}
        className={cn(
          'fixed z-50 flex max-h-[84dvh] flex-col overflow-hidden rounded-lg bg-raised text-ink shadow-modal outline-none',
          sheet,
          className,
        )}
        style={{ width: variant === 'sheet' ? undefined : `min(${width}px, calc(100vw - 32px))` }}
        {...props}
      >
        <div className={cn('flex items-center gap-2 px-4 pt-3 pb-2', hideTitle && 'sr-only')}>
          <D.Title className="flex-1 text-md font-semibold">{title}</D.Title>
          {showClose && (
            <D.Close asChild>
              <IconButton label="Close" size="sm">
                <X className="size-4" />
              </IconButton>
            </D.Close>
          )}
        </div>
        {children}
      </D.Content>
    </D.Portal>
  );
}

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  return (
    <T.Root delayDuration={350}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="z-50 rounded-sm bg-ink px-2 py-1 text-cap text-bg shadow-menu"
        >
          {content}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

export const TooltipProvider = T.Provider;

export function Switch({
  checked,
  onCheckedChange,
  label,
  id,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <S.Root
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
      className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-line-strong transition-colors data-[state=checked]:bg-primary disabled:opacity-50"
    >
      <S.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px]" />
    </S.Root>
  );
}
